import { type HolidayEntry, type TierKey } from "@reach/core";
import { CHANNEL_DEFAULTS } from "./config";

export const DEFAULT_MASTER_PROMPT = `Act as a senior store assistant for this Shopify store. 
Your goal is to provide accurate, helpful, and concise answers based strictly on the knowledge base provided. 
If you don't know the answer, politely redirect the customer to use one of our support channels.
Tone: Professional, friendly, and helpful.`;

export interface AppSettings {
  enabled: boolean;
  value: string;
  label: string;
  color: string;
  showOpen: boolean;
  showClosed: boolean;
  showOnMobile: boolean;
  showOnDesktop: boolean;
  order?: number;
}

export type KbItem = {
  id: string;
  title?: string;
  question?: string;
  reply?: string;
  customReply?: string;
  content?: string;
  suggestions?: string;
  keywords?: string;
  source?: string;
  sourceUrl?: string;
  targetUrl?: string;
  updatedAt?: string;
  createdAt?: string;
};

export type KbGap = {
  id: string;
  question: string;
  queryText?: string;
  hitCount?: number;
  hits?: number;
};

export type QueryLogItem = {
  queryText: string;
  kbTitle: string | null;
  isMatched: boolean;
  hits: number;
  lastAsked: string;
};

export type AnalyticsData = {
  period?: number;
  totalInteractions?: number;
  widgetOpens?: number;
  widgetOpensDelta?: number | null;
  uniqueVisitors?: number;
  uniqueVisitorsDelta?: number | null;
  channelClicks?: number;
  channelClicksDelta?: number | null;
  channelClickThroughRate?: number | null;
  channelClickBreakdown?: Array<{
    channel: string;
    clicks: number;
  }>;
  mobileOpens?: number;
  desktopOpens?: number;
  deviceSplit?: {
    mobile?: number;
    desktop?: number;
  };
  returningVisitors?: number;
  returnVisitorRate?: number | null;
  totalChats?: number;
  totalChatsDelta?: number | null;
  totalMessages?: number;
  totalMessagesDelta?: number | null;
  matchedMessages?: number;
  unmatchedMessages?: number;
  matchRate?: number | null;
  matchRateDelta?: number | null;
  helpfulCount?: number;
  unhelpfulCount?: number;
  feedbackScore?: number | null;
  feedbackScoreDelta?: number | null;
  suggestionClicks?: number;
  suggestionImpressions?: number;
  suggestionEngagementRate?: number | null;
  topClickedSuggestions?: Array<{
    text: string;
    clicks: number;
  }>;
  knowledgeGapCount?: number;
  knowledgeGapDelta?: number | null;
  topTopics?: Array<{
    kbId?: string;
    title: string;
    matchCount: number;
  }>;
  dailyTrend?: Array<{
    date?: string;
    day?: string;
    opens: number;
    chats?: number;
    messages?: number;
  }>;
  conversationsStarted?: number;
  messagesExchanged?: number;
  [key: string]: any;
};

export interface Settings {
  platform?: string;
  onboardingCompletedAt?: string;
  onboardingDismissedAt?: string;
  isEnabled: boolean;
  apps: Record<string, AppSettings>;
  botColor: string;
  anchorColor: string;
  supportAppsButtonColor: string;
  supportAppsIconColor: string;
  supportAppsContainerColor: string;
  supportAppsContainerOpacity: number;
  supportAppsTextColor: string;
  supportAppsFontFamily: string;
  supportAppsFontSize: number;
  supportAppsFontWeight: number;
  supportAppsButtonWrapperBg: string;
  supportAppsEntryLabel: string;
  supportAppsEntryTextColor: string;
  supportAppsEntryFontFamily: string;
  supportAppsEntryFontSize: number;
  supportAppsEntryFontWeight: number;
  chatbotButtonWrapperBg: string;
  chatbotEntryTextColor: string;
  chatbotEntryFontFamily: string;
  chatbotEntryFontSize: number;
  chatbotEntryFontWeight: number;
  chatbotHeaderTextColor: string;
  chatbotHeaderTagline: string;
  chatbotHeaderAvatarEnabled: boolean;
  chatbotHeaderStatusDotEnabled: boolean;
  chatbotHeaderFontFamily: string;
  chatbotHeaderFontSize: number;
  chatbotHeaderStyle: "solid" | "gradient";
  chatbotBodyFontFamily: string;
  chatbotBodyFontSize: number;
  chatbotSuggestionFontFamily: string;
  chatbotSuggestionFontSize: number;
  chatbotSuggestionFontWeight: number;
  chatbotScrollbarColor: string;
  chatbotContainerColor: string;
  chatbotContainerOpacity: number;
  globalFontFamily: string;
  globalFontSize: number;
  widgetPosition: "left" | "right";
  sideMargin: number;
  desktopSideMargin: number;
  mobileSideMargin?: number;
  bottomMargin: number;
  desktopBottomMargin: number;
  mobileBottomMargin?: number;
  buttonZIndex: number;
  supportAppsTitle: string;
  backInLabel: string;
  backInBgColor: string;
  backInTextColor: string;
  backInLabelColor: string;
  backInFontSize: number;
  backInFontFamily: string;
  backInFontWeight: number;
  anchorAvatar: string;
  chatbotAvatar: string;
  supportAppsAvatar: string;
  chatbotEntryAvatar: string;
  desktopButtonSize: "small" | "medium" | "large" | number;
  mobileButtonSize: "small" | "medium" | "large" | number;
  mobileRadius?: number;
  buttonRadius?: number;
  dockGlassStyle?: "glass" | "tinted" | "solid";
  dockOpacity?: number;
  dockTintColor?: string;
  dockMaxVisibleApps?: number;
  supportAppsLayout: "stacked" | "container" | "tabs";
  showStackedLabels: boolean;
  showContainerLabels: boolean;
  showStackedTooltips: boolean;
  showContainerTooltips: boolean;
  showAppsLabel: boolean;
  showChatbotLabel: boolean;
  timezone: string;
  startHour: number;
  endHour: number;
  weekendBehavior: "none" | "sunday" | "all";
  lunchEnabled: boolean;
  lunchStart: number;
  lunchEnd: number;
  holidayEnabled: boolean;
  holidays: (string | HolidayEntry)[];
  offlineMode: "follow_apps" | "chatbot" | "back_in_x" | "hide";
  planTier: TierKey;
  botName: string;
  welcomeMsg: string;
  initialSuggestions: string;
  chatbotCustomPrompt: string;
  chatbotTipEnabled: boolean;
  chatbotTipText: string;
  chatbotTipBgColor: string;
  chatbotTipTextColor: string;
  chatbotTipFontSize: number;
  chatbotTipFontFamily: string;
  chatbotTipFontWeight: number;
  launcherAnimation: "none" | "pulse" | "breath" | "float" | "glow" | "spin" | "rotate" | "rotate_gradient";
  launcherHoverEffect: "rotate" | "expand" | "lift" | "tilt" | "none";
  showStatusDot?: boolean;
  statusDotTooltipEnabled?: boolean;
  statusDotOnlineLabel?: string;
  statusDotLunchLabel?: string;
  statusDotClosedLabel?: string;
}

export const DEFAULT_SETTINGS: Settings = {
  isEnabled: true,
  apps: Object.keys(CHANNEL_DEFAULTS).reduce((acc, key, index) => ({
    ...acc,
    [key]: {
      enabled: false,
      value: "",
      label: CHANNEL_DEFAULTS[key].label,
      color: CHANNEL_DEFAULTS[key].color,
      showOpen: true,
      showClosed: true,
      showOnMobile: true,
      showOnDesktop: true,
      order: index,
    },
  }), {}),
  botColor: "#000000",
  anchorColor: "#000000",
  supportAppsButtonColor: "#312e81",
  supportAppsIconColor: "#ffffff",
  supportAppsContainerColor: "#ffffff",
  supportAppsContainerOpacity: 100,
  supportAppsTextColor: "#111111",
  supportAppsFontFamily: "inherit",
  supportAppsFontSize: 14,
  supportAppsFontWeight: 600,
  supportAppsButtonWrapperBg: "#312e81",
  supportAppsEntryLabel: "Get in Touch",
  supportAppsEntryTextColor: "#f4f4f5",
  supportAppsEntryFontFamily: "inherit",
  supportAppsEntryFontSize: 12,
  supportAppsEntryFontWeight: 700,
  launcherAnimation: "none",
  launcherHoverEffect: "none",
  showStatusDot: true,
  statusDotTooltipEnabled: true,
  statusDotOnlineLabel: "Online",
  statusDotLunchLabel: "Out for lunch",
  statusDotClosedLabel: "Closed",
  chatbotButtonWrapperBg: "#312e81",
  chatbotEntryTextColor: "#f4f4f5",
  chatbotEntryFontFamily: "inherit",
  chatbotEntryFontSize: 12,
  chatbotEntryFontWeight: 700,
  chatbotHeaderTextColor: "#ffffff",
  chatbotHeaderTagline: "Online",
  chatbotHeaderAvatarEnabled: true,
  chatbotHeaderStatusDotEnabled: true,
  chatbotHeaderFontFamily: "inherit",
  chatbotHeaderFontSize: 16,
  chatbotHeaderStyle: "solid",
  chatbotBodyFontFamily: "inherit",
  chatbotBodyFontSize: 14,
  chatbotSuggestionFontFamily: "inherit",
  chatbotSuggestionFontSize: 14,
  chatbotSuggestionFontWeight: 400,
  chatbotScrollbarColor: "#4f46e5",
  chatbotContainerColor: "#ffffff",
  chatbotContainerOpacity: 100,
  globalFontFamily: "Plus Jakarta Sans",
  globalFontSize: 14,
  widgetPosition: "right",
  sideMargin: 40,
  desktopSideMargin: 40,
  mobileSideMargin: 30,
  bottomMargin: 40,
  desktopBottomMargin: 40,
  mobileBottomMargin: 30,
  buttonZIndex: 999,
  supportAppsTitle: "How can we help?",
  backInLabel: "Back in",
  backInBgColor: "#09090b",
  backInTextColor: "#f4f4f5",
  backInLabelColor: "#a5b4fc",
  backInFontSize: 10,
  backInFontFamily: "inherit",
  backInFontWeight: 700,
  anchorAvatar: "",
  chatbotAvatar: "",
  supportAppsAvatar: "",
  chatbotEntryAvatar: "",
  desktopButtonSize: "medium",
  mobileButtonSize: "medium",
  buttonRadius: 40,
  mobileRadius: 34,
  dockGlassStyle: "solid",
  dockOpacity: 100,
  dockTintColor: "#000000",
  dockMaxVisibleApps: 4,
  supportAppsLayout: "stacked",
  showStackedLabels: true,
  showContainerLabels: true,
  showStackedTooltips: true,
  showContainerTooltips: true,
  showAppsLabel: false,
  showChatbotLabel: false,
  timezone: "America/New_York",
  startHour: 9,
  endHour: 17,
  weekendBehavior: "sunday",
  lunchEnabled: false,
  lunchStart: 12,
  lunchEnd: 13,
  holidayEnabled: false,
  holidays: [],
  offlineMode: "follow_apps",
  planTier: "free",
  botName: "Adam",
  welcomeMsg: "Hi! How can I help you today?",
  initialSuggestions: "Return Policy, Shipping Info, Contact Support",
  chatbotCustomPrompt: DEFAULT_MASTER_PROMPT,
  chatbotTipEnabled: false,
  chatbotTipText: "How can I help you?",
  chatbotTipBgColor: "#09090b",
  chatbotTipTextColor: "#f4f4f5",
  chatbotTipFontSize: 12,
  chatbotTipFontWeight: 600,
  chatbotTipFontFamily: "inherit",
};
